import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, formatDate, formatDateTime, scr } from '../../lib/api';
import { sha256Hex } from '../../lib/hash';
import { useAuth } from '../../lib/auth';
import type {
  ApplicationClass,
  ApplicationDocument,
  ConstraintReport,
  Page,
  PlanningApplication,
  PublicObjection,
  Referral,
} from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import {
  BackLink,
  EmptyState,
  ErrorNote,
  Field,
  LoadMoreButton,
  Modal,
  PageHeader,
  Spinner,
  StatCard,
  StatusBadge,
  TabBar,
} from '../../ui/components';

const STAFF_LIST_STATES = [
  'SUBMITTED',
  'VALIDATION',
  'REFERRALS_NOTICE',
  'ASSESSMENT',
  'COMMITTEE',
  'APPROVED',
  'REFUSED',
  'PERMIT_ISSUED',
  'INSPECTIONS',
  'OCCUPANCY_CERTIFIED',
  'WITHDRAWN',
] as const;

const LAYER_LABELS: Record<string, string> = {
  ZONING: 'Zoning',
  FLOOD: 'Flood risk',
  COASTAL_SETBACK: 'Coastal setback',
  HERITAGE: 'Heritage',
  AVIATION_HEIGHT: 'Aviation height',
};

function ConstraintSummary({ report }: { report: ConstraintReport | null }) {
  if (!report) return <span className="text-xs text-slate-400">Not yet run</span>;
  const hits = Object.entries(report).filter(([, v]) => v?.intersects);
  if (hits.length === 0) return <span className="text-xs text-emerald-600">Clear on all layers</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {hits.map(([code]) => (
        <span key={code} className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 ring-1 ring-amber-200">
          {LAYER_LABELS[code] ?? code}
        </span>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// List
// ═══════════════════════════════════════════════════════════════════════

export function PlanningApplications() {
  const { persona } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState<string | null>(null);
  const [rows, setRows] = useState<PlanningApplication[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [sweepBusy, setSweepBusy] = useState(false);
  const [sweepResult, setSweepResult] = useState<string | null>(null);

  const classesQ = useApiQuery<{ items: ApplicationClass[] }>('/api/v1/m17/application-classes');

  const load = async (reset: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', '25');
      if (status) params.set('status', status);
      if (q.trim()) params.set('q', q.trim());
      if (!reset && cursor) params.set('cursor', cursor);
      const page = await api.get<Page<PlanningApplication>>(`/api/v1/m17/applications?${params.toString()}`);
      setRows((prev) => (reset ? page.items : [...prev, ...page.items]));
      setNextCursor(page.nextCursor);
      setCursor(page.nextCursor);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not load applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canSweep = persona?.roles.includes('SENIOR_PLANNER') ?? false;

  const runSweep = async () => {
    setSweepBusy(true);
    setSweepResult(null);
    try {
      const res = await api.post<{ sweptAt: string; deemedCount: number }>('/api/v1/m17/referrals/sweep-sla');
      setSweepResult(`Swept at ${formatDateTime(res.sweptAt)} — ${res.deemedCount} referral(s) deemed no-objection.`);
    } catch (e) {
      setSweepResult(e instanceof ApiError ? `${e.message} (${e.code})` : 'Sweep failed');
    } finally {
      setSweepBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Development Applications"
        subtitle="Submission -> validation -> referrals/public notice -> assessment -> committee/delegated decision -> permit -> inspections -> occupancy."
        actions={
          <>
            {canSweep ? (
              <button className="btn-secondary" onClick={runSweep} disabled={sweepBusy}>
                {sweepBusy ? 'Sweeping…' : 'Run referral SLA sweep'}
              </button>
            ) : null}
            <button className="btn-primary" onClick={() => setShowNew(true)}>
              + New application
            </button>
          </>
        }
      />

      {sweepResult ? (
        <div className="mb-4">
          <ErrorNote message={sweepResult} />
        </div>
      ) : null}

      <div className="card mb-4 flex flex-wrap items-end gap-3 p-4">
        <Field label="Status" className="w-48">
          <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {STAFF_LIST_STATES.map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Search" className="min-w-[220px] flex-1">
          <input
            className="field-input"
            placeholder="Ref no, parcel no, or applicant id"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load(true)}
          />
        </Field>
        <button className="btn-secondary" onClick={() => load(true)}>
          Filter
        </button>
      </div>

      {error ? (
        <div className="mb-4">
          <ErrorNote message={error} />
        </div>
      ) : null}

      <div className="card overflow-x-auto">
        <table className="w-full min-w-[760px] border-collapse">
          <thead>
            <tr>
              <th className="table-head">Ref no</th>
              <th className="table-head">Class</th>
              <th className="table-head">Parcel</th>
              <th className="table-head">Applicant</th>
              <th className="table-head">State</th>
              <th className="table-head">Fee</th>
              <th className="table-head">Submitted</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id} className="table-row-link" onClick={() => navigate(`/planning/applications/${a.id}`)}>
                <td className="table-cell font-semibold text-slate-800">{a.refNo}</td>
                <td className="table-cell">{a.classCode.replaceAll('_', ' ')}</td>
                <td className="table-cell">{a.parcelNo}</td>
                <td className="table-cell">{a.applicantId}</td>
                <td className="table-cell">
                  <StatusBadge status={a.state} />
                </td>
                <td className="table-cell">
                  <StatusBadge status={a.feeStatus} /> <span className="text-xs text-slate-400">{scr(a.feeAmountScr)}</span>
                </td>
                <td className="table-cell text-xs text-slate-500">{formatDate(a.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading ? <Spinner label="Loading applications…" /> : null}
        {!loading && rows.length === 0 ? <EmptyState title="No applications found" hint="Try widening your filters, or log a new application." /> : null}
      </div>
      <LoadMoreButton nextCursor={nextCursor} loading={loading} onClick={() => load(false)} />

      {showNew ? (
        <NewApplicationModal
          classes={classesQ.data?.items ?? []}
          onClose={() => setShowNew(false)}
          onCreated={(app) => {
            setShowNew(false);
            navigate(`/planning/applications/${app.id}`);
          }}
        />
      ) : null}
    </div>
  );
}

function NewApplicationModal({
  classes,
  onClose,
  onCreated,
}: {
  classes: ApplicationClass[];
  onClose: () => void;
  onCreated: (app: PlanningApplication) => void;
}) {
  const [classCode, setClassCode] = useState(classes[0]?.code ?? '');
  const [parcelNo, setParcelNo] = useState('');
  const [description, setDescription] = useState('');
  const [use, setUse] = useState('');
  const [floorArea, setFloorArea] = useState('');
  const [height, setHeight] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const app = await api.post<PlanningApplication>('/api/v1/m17/applications', {
        classCode,
        parcelNo: parcelNo.trim(),
        proposal: {
          description,
          use,
          floorArea: floorArea ? Number(floorArea) : undefined,
          height: height ? Number(height) : undefined,
        },
        documents: [],
      });
      onCreated(app);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not submit application');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New development application" onClose={onClose} wide>
      <p className="mb-4 text-xs text-slate-500">
        POST /api/v1/m17/applications — filed under your own signed-in identity (this portal has no citizen-assisted
        "file on behalf of" flow yet; see the build report). A C9 GeoServe-validated parcel and an active application
        class are both required.
      </p>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Application class">
          <select className="field-input" value={classCode} onChange={(e) => setClassCode(e.target.value)}>
            {classes.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Parcel number">
          <input className="field-input" placeholder="e.g. PS/1234/2026" value={parcelNo} onChange={(e) => setParcelNo(e.target.value)} />
        </Field>
        <Field label="Proposed use" className="sm:col-span-2">
          <input className="field-input" placeholder="e.g. RESIDENTIAL, RESTAURANT" value={use} onChange={(e) => setUse(e.target.value)} />
        </Field>
        <Field label="Proposal description" className="sm:col-span-2">
          <textarea className="field-input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Field label="Floor area (m², optional)">
          <input className="field-input" type="number" min="0" value={floorArea} onChange={(e) => setFloorArea(e.target.value)} />
        </Field>
        <Field label="Height (m, optional)">
          <input className="field-input" type="number" min="0" value={height} onChange={(e) => setHeight(e.target.value)} />
        </Field>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button className="btn-primary" onClick={submit} disabled={busy || !classCode || !parcelNo.trim() || description.length < 3 || use.length < 2}>
          {busy ? 'Submitting…' : 'Submit application'}
        </button>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Detail
// ═══════════════════════════════════════════════════════════════════════

const TABS = [
  { key: 'overview', label: 'Overview & lifecycle' },
  { key: 'referrals', label: 'Referrals & objections' },
  { key: 'permit', label: 'Permit, inspections & occupancy' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

export function PlanningApplicationDetail() {
  const { id = '' } = useParams();
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const appQ = useApiQuery<PlanningApplication & { documents: ApplicationDocument[] }>(`/api/v1/m17/applications/${id}`);
  const [tab, setTab] = useState<TabKey>('overview');
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNote, setActionNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const app = appQ.data;

  const act = async (fn: () => Promise<unknown>, note?: string) => {
    setBusy(true);
    setActionError(null);
    setActionNote(null);
    try {
      await fn();
      appQ.reload();
      if (note) setActionNote(note);
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  if (appQ.loading) return <Spinner label="Loading application…" />;
  if (appQ.error || !app) return <ErrorNote message={appQ.error ?? 'Application not found'} />;

  const isValidationOfficer = roles.includes('VALIDATION_OFFICER');
  const isPlannerOrSenior = roles.includes('PLANNER') || roles.includes('SENIOR_PLANNER');
  const isSeniorPlanner = roles.includes('SENIOR_PLANNER');

  return (
    <div>
      <BackLink to="/planning/applications" />
      <PageHeader
        title={app.refNo}
        subtitle={`${app.classCode.replaceAll('_', ' ')} · parcel ${app.parcelNo} · applicant ${app.applicantId}`}
        actions={<StatusBadge status={app.state} />}
      />

      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}
      {actionNote ? (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{actionNote}</div>
      ) : null}

      <TabBar tabs={TABS as unknown as Array<{ key: TabKey; label: string }>} active={tab} onChange={setTab} />

      {tab === 'overview' ? (
        <OverviewTab
          app={app}
          busy={busy}
          canValidate={isValidationOfficer}
          canAssess={isPlannerOrSenior}
          canDelegatedDecide={isSeniorPlanner}
          canIssuePermit={isPlannerOrSenior}
          onValidate={() => act(() => api.post(`/api/v1/m17/applications/${id}/validate`), 'Validation processed.')}
          onAdvance={() => act(() => api.post(`/api/v1/m17/applications/${id}/advance-to-assessment`), 'Moved to assessment.')}
          onAssessment={(body) => act(() => api.post(`/api/v1/m17/applications/${id}/assessment`, body), 'Assessment recorded.')}
          onDelegatedDecision={(body) => act(() => api.post(`/api/v1/m17/applications/${id}/delegated-decision`, body), 'Delegated decision recorded.')}
          onWithdraw={(reason) => act(() => api.post(`/api/v1/m17/applications/${id}/withdraw`, { reason }), 'Application withdrawn.')}
          onDrawings={(documents) => act(() => api.post(`/api/v1/m17/applications/${id}/drawings`, { documents }), 'Drawing set submitted.')}
          onIssuePermit={(conditions) =>
            act(async () => {
              const permit = await api.post<{ id: string }>(`/api/v1/m17/applications/${id}/permit`, { conditions });
              localStorage.setItem(`landuse.m17.permitByApplication.${id}`, permit.id);
            }, 'Permit issued.')
          }
        />
      ) : null}

      {tab === 'referrals' ? <ReferralsObjectionsTab applicationId={id} canRespond={isPlannerOrSenior} canViewObjections={isPlannerOrSenior || roles.includes('COMMITTEE_SECRETARIAT')} /> : null}

      {tab === 'permit' ? <PermitInspectionsTab applicationId={id} roles={roles} /> : null}
    </div>
  );
}

function OverviewTab({
  app,
  busy,
  canValidate,
  canAssess,
  canDelegatedDecide,
  canIssuePermit,
  onValidate,
  onAdvance,
  onAssessment,
  onDelegatedDecision,
  onWithdraw,
  onDrawings,
  onIssuePermit,
}: {
  app: PlanningApplication & { documents: ApplicationDocument[] };
  busy: boolean;
  canValidate: boolean;
  canAssess: boolean;
  canDelegatedDecide: boolean;
  canIssuePermit: boolean;
  onValidate: () => void;
  onAdvance: () => void;
  onAssessment: (body: { recommendation: string; conditions: string[]; reasons: string[]; notes?: string }) => void;
  onDelegatedDecision: (body: { decision: string; conditions: string[]; reasons: string[] }) => void;
  onWithdraw: (reason?: string) => void;
  onDrawings: (documents: Array<{ docType: string; docRef: string; sha256: string; fileName?: string }>) => void;
  onIssuePermit: (conditions?: string[]) => void;
}) {
  const [recommendation, setRecommendation] = useState<'APPROVE' | 'REFUSE'>('APPROVE');
  const [reasons, setReasons] = useState('');
  const [conditions, setConditions] = useState('');
  const [notes, setNotes] = useState('');
  const [delegatedDecision, setDelegatedDecision] = useState<'APPROVED' | 'REFUSED'>('APPROVED');
  const [withdrawReason, setWithdrawReason] = useState('');
  const [docType, setDocType] = useState('');
  const [docRef, setDocRef] = useState('');
  const [permitConditions, setPermitConditions] = useState('');

  const isTerminal = app.state === 'WITHDRAWN' || app.decidedAt !== null;
  const list = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);

  const [computedHash, setComputedHash] = useState('');
  const addDrawing = async () => {
    const hash = docRef ? await sha256Hex(docRef) : '';
    setComputedHash(hash);
    onDrawings([{ docType, docRef, sha256: hash, fileName: undefined }]);
  };

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <div className="space-y-5 lg:col-span-2">
        <div className="card p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-700">Proposal</h2>
          <dl className="space-y-1.5 text-sm">
            <div className="flex justify-between"><dt className="text-slate-500">Use</dt><dd className="font-medium text-slate-800">{app.proposal.use}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Floor area</dt><dd className="font-medium text-slate-800">{app.proposal.floorArea ?? '—'} m²</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Height</dt><dd className="font-medium text-slate-800">{app.proposal.height ?? '—'} m</dd></div>
          </dl>
          <p className="mt-3 border-t border-slate-100 pt-3 text-sm text-slate-600">{app.proposal.description}</p>
        </div>

        <div className="card p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-700">Constraint report</h2>
          <ConstraintSummary report={app.constraintReport} />
        </div>

        <div className="card p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-700">Documents (drawing version {app.drawingVersion})</h2>
          {app.documents.length === 0 ? <EmptyState title="No documents on file" /> : (
            <ul className="mb-3 space-y-1 text-xs text-slate-600">
              {app.documents.map((d) => (
                <li key={d.id} className="flex justify-between border-b border-slate-100 py-1">
                  <span>v{d.version} · {d.docType}</span>
                  <span className="font-mono text-slate-400">{d.sha256.slice(0, 12)}…</span>
                </li>
              ))}
            </ul>
          )}
          {!isTerminal ? (
            <div className="flex flex-wrap items-end gap-2 border-t border-slate-100 pt-3">
              <Field label="Doc type" className="w-40">
                <input className="field-input" value={docType} onChange={(e) => setDocType(e.target.value)} placeholder="SITE_PLAN" />
              </Field>
              <Field label="Doc reference" className="min-w-[180px] flex-1">
                <input className="field-input" value={docRef} onChange={(e) => setDocRef(e.target.value)} placeholder="object-storage ref" />
              </Field>
              <button className="btn-secondary" disabled={busy || !docType || !docRef} onClick={addDrawing}>
                Submit new drawing set
              </button>
            </div>
          ) : null}
          {computedHash ? <p className="mt-1 text-[11px] text-slate-400">SHA-256 computed client-side over the reference text: {computedHash.slice(0, 20)}…</p> : null}
        </div>

        {canAssess && app.state === 'ASSESSMENT' ? (
          <div className="card p-5">
            <h2 className="mb-3 text-sm font-bold text-slate-700">Record assessment recommendation</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Recommendation">
                <select className="field-input" value={recommendation} onChange={(e) => setRecommendation(e.target.value as 'APPROVE' | 'REFUSE')}>
                  <option value="APPROVE">Approve</option>
                  <option value="REFUSE">Refuse</option>
                </select>
              </Field>
              <Field label="Conditions (comma-separated)">
                <input className="field-input" value={conditions} onChange={(e) => setConditions(e.target.value)} />
              </Field>
              <Field label="Reasons (required if refusing)" className="sm:col-span-2">
                <input className="field-input" value={reasons} onChange={(e) => setReasons(e.target.value)} />
              </Field>
              <Field label="Notes" className="sm:col-span-2">
                <textarea className="field-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </Field>
            </div>
            <button
              className="btn-primary mt-3"
              disabled={busy}
              onClick={() => onAssessment({ recommendation, conditions: list(conditions), reasons: list(reasons), notes: notes || undefined })}
            >
              Record recommendation
            </button>
          </div>
        ) : null}

        {canDelegatedDecide && app.state === 'ASSESSMENT' ? (
          <div className="card p-5">
            <h2 className="mb-3 text-sm font-bold text-slate-700">Delegated decision (senior planner)</h2>
            <p className="mb-3 text-xs text-slate-500">Only valid for delegated-eligible application classes and once an assessment recommendation is on file — the API rejects otherwise.</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Decision">
                <select className="field-input" value={delegatedDecision} onChange={(e) => setDelegatedDecision(e.target.value as 'APPROVED' | 'REFUSED')}>
                  <option value="APPROVED">Approved</option>
                  <option value="REFUSED">Refused</option>
                </select>
              </Field>
              <Field label="Conditions / reasons (comma-separated)">
                <input className="field-input" value={conditions} onChange={(e) => setConditions(e.target.value)} />
              </Field>
            </div>
            <button
              className="btn-primary mt-3"
              disabled={busy}
              onClick={() => onDelegatedDecision({ decision: delegatedDecision, conditions: list(conditions), reasons: list(conditions) })}
            >
              Record delegated decision
            </button>
          </div>
        ) : null}

        {canIssuePermit && app.state === 'APPROVED' ? (
          <div className="card p-5">
            <h2 className="mb-3 text-sm font-bold text-slate-700">Issue building permit</h2>
            <Field label="Conditions override (optional, comma-separated — defaults to approval conditions)">
              <input className="field-input" value={permitConditions} onChange={(e) => setPermitConditions(e.target.value)} />
            </Field>
            <button className="btn-success mt-3" disabled={busy} onClick={() => onIssuePermit(permitConditions ? list(permitConditions) : undefined)}>
              Issue permit
            </button>
          </div>
        ) : null}
      </div>

      <div className="space-y-5">
        <div className="card p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-700">Lifecycle actions</h2>
          <div className="space-y-2">
            {canValidate && ['SUBMITTED', 'VALIDATION'].includes(app.state) ? (
              <button className="btn-primary w-full" disabled={busy || app.feeStatus !== 'PAID'} onClick={onValidate}>
                Validate {app.feeStatus !== 'PAID' ? '(fee unpaid)' : ''}
              </button>
            ) : null}
            {canAssess && app.state === 'REFERRALS_NOTICE' ? (
              <button className="btn-primary w-full" disabled={busy} onClick={onAdvance}>
                Advance to assessment
              </button>
            ) : null}
            {!isTerminal ? (
              <div className="flex gap-2">
                <input className="field-input" placeholder="Withdrawal reason" value={withdrawReason} onChange={(e) => setWithdrawReason(e.target.value)} />
                <button className="btn-danger shrink-0" disabled={busy} onClick={() => onWithdraw(withdrawReason || undefined)}>
                  Withdraw
                </button>
              </div>
            ) : null}
            {!canValidate && !canAssess && !canIssuePermit ? <p className="text-xs text-slate-400">No lifecycle actions available for your role at this stage.</p> : null}
          </div>
        </div>

        <div className="card p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-700">Key dates</h2>
          <dl className="space-y-1.5 text-xs">
            <div className="flex justify-between"><dt className="text-slate-500">Validation due</dt><dd>{app.validationDueAt ? new Date(app.validationDueAt).toLocaleDateString('en-GB') : '—'}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Notice opened</dt><dd>{app.noticeOpenedAt ? new Date(app.noticeOpenedAt).toLocaleDateString('en-GB') : '—'}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Notice closes</dt><dd>{app.noticeClosesAt ? new Date(app.noticeClosesAt).toLocaleDateString('en-GB') : '—'}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Decided</dt><dd>{app.decidedAt ? new Date(app.decidedAt).toLocaleDateString('en-GB') : '—'}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Decision route</dt><dd>{app.decisionRoute ?? '—'}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Appeal deadline</dt><dd>{app.appealDeadline ? new Date(app.appealDeadline).toLocaleDateString('en-GB') : '—'}</dd></div>
          </dl>
        </div>

        <StatCard label="Application fee" value={scr(app.feeAmountScr)} hint={`${app.feeStatus} · ${app.feeIntentRefNo ?? 'no intent'}`} tone={app.feeStatus === 'PAID' ? 'good' : 'warn'} />
      </div>
    </div>
  );
}

function ReferralsObjectionsTab({ applicationId, canRespond, canViewObjections }: { applicationId: string; canRespond: boolean; canViewObjections: boolean }) {
  const referralsQ = useApiQuery<{ items: Referral[] }>(`/api/v1/m17/applications/${applicationId}/referrals`);
  const objectionsQ = useApiQuery<{ items: PublicObjection[] }>(canViewObjections ? `/api/v1/m17/applications/${applicationId}/objections` : null);
  const [respondingId, setRespondingId] = useState<string | null>(null);
  const [decision, setDecision] = useState<'NO_OBJECTION' | 'CONDITIONAL' | 'OBJECTION'>('NO_OBJECTION');
  const [comments, setComments] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [objName, setObjName] = useState('');
  const [objContact, setObjContact] = useState('');
  const [objGrounds, setObjGrounds] = useState('');
  const [objBusy, setObjBusy] = useState(false);
  const [objError, setObjError] = useState<string | null>(null);

  const respond = async (referralId: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m17/referrals/${referralId}/respond`, { decision, comments: comments || undefined, conditions: [] });
      setRespondingId(null);
      setComments('');
      referralsQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not record response');
    } finally {
      setBusy(false);
    }
  };

  const logObjection = async () => {
    setObjBusy(true);
    setObjError(null);
    try {
      await api.post(`/api/v1/m17/applications/${applicationId}/objections`, { objectorName: objName, objectorContact: objContact || undefined, grounds: objGrounds });
      setObjName('');
      setObjContact('');
      setObjGrounds('');
      objectionsQ.reload();
    } catch (e) {
      setObjError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not log objection');
    } finally {
      setObjBusy(false);
    }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="card p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-700">Referrals</h2>
        {referralsQ.error ? <ErrorNote message={referralsQ.error} /> : null}
        {referralsQ.loading ? <Spinner /> : null}
        {referralsQ.data?.items.length === 0 ? <EmptyState title="No referrals fanned out yet" hint="Referrals open once the application is validated." /> : null}
        <div className="space-y-3">
          {referralsQ.data?.items.map((r) => (
            <div key={r.id} className="rounded-lg border border-slate-200 p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-800">{r.mda}</span>
                <StatusBadge status={r.status} />
              </div>
              <div className="mt-1 text-xs text-slate-500">SLA due {formatDate(r.slaDueAt)} · deemed threshold {formatDate(r.deemedThresholdAt)}</div>
              {r.decision ? <div className="mt-1 text-xs"><StatusBadge status={r.decision} /> {r.comments}</div> : null}
              {canRespond && r.status === 'PENDING' ? (
                respondingId === r.id ? (
                  <div className="mt-2 space-y-2 border-t border-slate-100 pt-2">
                    {error ? <ErrorNote message={error} /> : null}
                    <select className="field-input" value={decision} onChange={(e) => setDecision(e.target.value as typeof decision)}>
                      <option value="NO_OBJECTION">No objection</option>
                      <option value="CONDITIONAL">Conditional</option>
                      <option value="OBJECTION">Objection</option>
                    </select>
                    <input className="field-input" placeholder="Comments" value={comments} onChange={(e) => setComments(e.target.value)} />
                    <div className="flex gap-2">
                      <button className="btn-primary btn-xs" disabled={busy} onClick={() => respond(r.id)}>Save response</button>
                      <button className="btn-secondary btn-xs" onClick={() => setRespondingId(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button className="btn-secondary btn-xs mt-2" onClick={() => setRespondingId(r.id)}>
                    Record response
                  </button>
                )
              ) : null}
            </div>
          ))}
        </div>
      </div>

      <div className="card p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-700">Public objections</h2>
        {!canViewObjections ? <p className="text-xs text-slate-400">Requires PLANNER, SENIOR_PLANNER, or COMMITTEE_SECRETARIAT to view the bundle.</p> : null}
        {objectionsQ.error ? <ErrorNote message={objectionsQ.error} /> : null}
        {objectionsQ.loading ? <Spinner /> : null}
        {canViewObjections && objectionsQ.data?.items.length === 0 ? <EmptyState title="No objections received" /> : null}
        <div className="mb-3 space-y-2">
          {objectionsQ.data?.items.map((o) => (
            <div key={o.id} className="rounded-lg border border-slate-200 p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-800">{o.objectorName}</span>
                <StatusBadge status={o.status} />
              </div>
              <p className="mt-1 text-xs text-slate-600">{o.grounds}</p>
            </div>
          ))}
        </div>
        <div className="border-t border-slate-100 pt-3">
          <p className="mb-2 text-xs text-slate-500">Log an objection received by post/counter/phone (only accepted while the public notice window is open).</p>
          {objError ? <ErrorNote message={objError} /> : null}
          <div className="space-y-2">
            <input className="field-input" placeholder="Objector name" value={objName} onChange={(e) => setObjName(e.target.value)} />
            <input className="field-input" placeholder="Contact (optional)" value={objContact} onChange={(e) => setObjContact(e.target.value)} />
            <textarea className="field-input" rows={2} placeholder="Grounds for objection" value={objGrounds} onChange={(e) => setObjGrounds(e.target.value)} />
            <button className="btn-secondary" disabled={objBusy || objName.length < 2 || objGrounds.length < 5} onClick={logObjection}>
              Log objection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const INSPECTION_STAGES = ['SETTING_OUT', 'FOUNDATION', 'RING_BEAM', 'ROOF', 'COMPLETION'] as const;

function PermitInspectionsTab({ applicationId, roles }: { applicationId: string; roles: string[] }) {
  const cacheKey = `landuse.m17.permitByApplication.${applicationId}`;
  const [permitId, setPermitId] = useState(() => localStorage.getItem(cacheKey) ?? '');
  const [manualId, setManualId] = useState('');
  const permitQ = useApiQuery<{ id: string; approvalRef: string; status: string; conditions: string[] | null; commencementNoticeAt: string | null }>(
    permitId ? `/api/v1/m17/permits/${permitId}` : null,
  );
  const inspectionsQ = useApiQuery<{ items: Array<{ id: string; stage: string; status: string; result: string | null; notes: string | null; requestedDate: string | null; inspectedAt: string | null }> }>(
    permitId ? `/api/v1/m17/permits/${permitId}/inspections` : null,
  );
  const [stage, setStage] = useState<(typeof INSPECTION_STAGES)[number]>('SETTING_OUT');
  const [requestedDate, setRequestedDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultFor, setResultFor] = useState<string | null>(null);
  const [result, setResult] = useState<'PASS' | 'FAIL' | 'HOLD'>('PASS');
  const [resultNotes, setResultNotes] = useState('');
  const [holdPoints, setHoldPoints] = useState('');
  const [certSerial, setCertSerial] = useState('');
  const [certResult, setCertResult] = useState<string | null>(null);

  const canInspectResult = roles.includes('BUILDING_INSPECTOR');
  const canIssueOccupancy = roles.includes('BUILDING_INSPECTOR') || roles.includes('SENIOR_PLANNER');

  const bookInspection = async () => {
    if (!permitId) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m17/permits/${permitId}/inspections`, { stage, requestedDate: requestedDate || undefined });
      inspectionsQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not book inspection');
    } finally {
      setBusy(false);
    }
  };

  const recordResult = async (inspectionId: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m17/inspections/${inspectionId}/result`, {
        result,
        notes: resultNotes || undefined,
        holdPoints: holdPoints ? holdPoints.split(',').map((s) => s.trim()).filter(Boolean) : [],
        photos: [],
      });
      setResultFor(null);
      inspectionsQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not record inspection result');
    } finally {
      setBusy(false);
    }
  };

  const issueOccupancy = async () => {
    if (!permitId) return;
    setBusy(true);
    setError(null);
    try {
      const cert = await api.post<{ certSerial: string }>(`/api/v1/m17/permits/${permitId}/occupancy-certificate`, {});
      setCertSerial(cert.certSerial);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not issue occupancy certificate');
    } finally {
      setBusy(false);
    }
  };

  const verifyCert = async () => {
    if (!certSerial) return;
    try {
      const res = await api.get<{ valid: boolean; status: string }>(`/api/v1/m17/occupancy-certificates/${encodeURIComponent(certSerial)}`);
      setCertResult(`${res.valid ? 'VALID' : 'INVALID'} — ${res.status}`);
    } catch (e) {
      setCertResult(e instanceof ApiError ? `${e.message} (${e.code})` : 'Verification failed');
    }
  };

  return (
    <div className="space-y-5">
      <div className="card p-5">
        <h2 className="mb-2 text-sm font-bold text-slate-700">Building permit</h2>
        <p className="mb-3 text-xs text-slate-500">
          M17 has no GET /applications/:id/permit reverse lookup — this portal remembers the permit id (client-side
          only) once you issue it from the Overview tab. Paste a permit id below if it isn't cached in this browser.
        </p>
        <div className="mb-3 flex gap-2">
          <input className="field-input" placeholder="Permit id" value={manualId} onChange={(e) => setManualId(e.target.value)} />
          <button
            className="btn-secondary shrink-0"
            onClick={() => {
              localStorage.setItem(cacheKey, manualId);
              setPermitId(manualId);
            }}
          >
            Open
          </button>
        </div>
        {permitQ.error ? <ErrorNote message={permitQ.error} /> : null}
        {permitId && permitQ.data ? (
          <dl className="space-y-1 text-sm">
            <div className="flex justify-between"><dt className="text-slate-500">Approval ref</dt><dd className="font-medium">{permitQ.data.approvalRef}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Status</dt><dd><StatusBadge status={permitQ.data.status} /></dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Commencement notice</dt><dd>{permitQ.data.commencementNoticeAt ? formatDate(permitQ.data.commencementNoticeAt) : 'Not yet submitted by applicant'}</dd></div>
          </dl>
        ) : !permitId ? (
          <EmptyState title="No permit known for this application yet" hint="Issue one from the Overview tab once the application is APPROVED." />
        ) : null}
      </div>

      {permitId ? (
        <div className="card p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-700">Staged inspections</h2>
          {error ? <ErrorNote message={error} /> : null}
          {inspectionsQ.loading ? <Spinner /> : null}
          <div className="mb-3 space-y-2">
            {inspectionsQ.data?.items.map((insp) => (
              <div key={insp.id} className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-800">{insp.stage.replaceAll('_', ' ')}</span>
                  <div className="flex items-center gap-1.5">
                    <StatusBadge status={insp.status} />
                    {insp.result ? <StatusBadge status={insp.result} /> : null}
                  </div>
                </div>
                {insp.notes ? <p className="mt-1 text-xs text-slate-500">{insp.notes}</p> : null}
                {canInspectResult && insp.status === 'BOOKED' ? (
                  resultFor === insp.id ? (
                    <div className="mt-2 space-y-2 border-t border-slate-100 pt-2">
                      <select className="field-input" value={result} onChange={(e) => setResult(e.target.value as typeof result)}>
                        <option value="PASS">Pass</option>
                        <option value="FAIL">Fail</option>
                        <option value="HOLD">Hold</option>
                      </select>
                      {result !== 'PASS' ? (
                        <input className="field-input" placeholder="Hold points (comma-separated, required)" value={holdPoints} onChange={(e) => setHoldPoints(e.target.value)} />
                      ) : null}
                      <textarea className="field-input" rows={2} placeholder="Notes" value={resultNotes} onChange={(e) => setResultNotes(e.target.value)} />
                      <div className="flex gap-2">
                        <button className="btn-primary btn-xs" disabled={busy} onClick={() => recordResult(insp.id)}>Save result</button>
                        <button className="btn-secondary btn-xs" onClick={() => setResultFor(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button className="btn-secondary btn-xs mt-2" onClick={() => setResultFor(insp.id)}>
                      Record result
                    </button>
                  )
                ) : null}
              </div>
            ))}
            {inspectionsQ.data?.items.length === 0 ? <EmptyState title="No inspections booked yet" /> : null}
          </div>
          <div className="flex flex-wrap items-end gap-2 border-t border-slate-100 pt-3">
            <Field label="Stage" className="w-40">
              <select className="field-input" value={stage} onChange={(e) => setStage(e.target.value as typeof stage)}>
                {INSPECTION_STAGES.map((s) => (
                  <option key={s} value={s}>{s.replaceAll('_', ' ')}</option>
                ))}
              </select>
            </Field>
            <Field label="Requested date (optional)">
              <input className="field-input" type="date" value={requestedDate} onChange={(e) => setRequestedDate(e.target.value)} />
            </Field>
            <button className="btn-secondary" disabled={busy} onClick={bookInspection}>Book stage</button>
          </div>
        </div>
      ) : null}

      {permitId ? (
        <div className="card p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-700">Occupancy certificate</h2>
          {canIssueOccupancy ? (
            <button className="btn-success" disabled={busy} onClick={issueOccupancy}>
              Issue occupancy certificate
            </button>
          ) : (
            <p className="text-xs text-slate-400">Requires BUILDING_INSPECTOR or SENIOR_PLANNER — every inspection stage must have PASSED.</p>
          )}
          {certSerial ? (
            <div className="mt-3 border-t border-slate-100 pt-3">
              <p className="text-sm font-semibold text-emerald-700">Issued: {certSerial}</p>
              <div className="mt-2 flex gap-2">
                <button className="btn-secondary btn-xs" onClick={verifyCert}>Verify (public lookup)</button>
                {certResult ? <span className="text-xs text-slate-500">{certResult}</span> : null}
              </div>
            </div>
          ) : null}
          <div className="mt-3 border-t border-slate-100 pt-3">
            <p className="mb-2 text-xs text-slate-500">Verify any occupancy certificate by serial (public endpoint):</p>
            <div className="flex gap-2">
              <input className="field-input" placeholder="Cert serial" value={certSerial} onChange={(e) => setCertSerial(e.target.value)} />
              <button className="btn-secondary shrink-0" onClick={verifyCert}>Verify</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
